'use client';
import React, { forwardRef, useEffect, useState } from 'react';
import { useFormControlContext } from '@dr-pam/common-components/Components/Form/FormControl';
import { useFormContext } from '@dr-pam/common-components/Components/Form/Form';
import CustomLink from '../article/CustomLink';

export type TextInputProps = {
	className?: string;
	type?: HTMLInputElement['type'];
	placeholder?: HTMLInputElement['placeholder'];
	disabled?: boolean;
	autoComplete?: string;
	value?: string;
	onChange?: (value: string) => void;
	onBlur?: (value: string) => void;
	showPasswordToggle?: boolean;
};

export default forwardRef<HTMLInputElement, TextInputProps>(function TextInput(props: TextInputProps, ref) {
	const { className, type, placeholder, autoComplete, value, onChange, onBlur, showPasswordToggle } = props;

	const [inputValue, setInputValue] = useState(value ?? '');
	const [showPassword, setShowPassword] = useState(false);

	useEffect(() => {
		setInputValue(value ?? '');
	}, [value]);

	const formContext = useFormContext();
	const formControl = useFormControlContext();

	const disabled = props.disabled || formControl?.disabled || formContext?.disabled;

	const formInputProps =
		formContext && formControl
			? formContext.form.getInputProps(formControl.name)
			: {
					value: inputValue,
					onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
						const val = e.target.value;
						setInputValue(val);
						onChange?.(val);
					},
					onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
						const val = e.target.value;
						setInputValue(val);
						onBlur?.(val);
					},
				};

	const currentValue = formInputProps.value || inputValue || '';

	const isPasswordEmpty = !currentValue || currentValue.toString().length === 0;
	const isToggleDisabled = disabled || isPasswordEmpty;

	useEffect(() => {
		if (isPasswordEmpty && showPassword) {
			setShowPassword(false);
		}
	}, [isPasswordEmpty, showPassword]);

	const shouldShowToggle = showPasswordToggle || type === 'password';
	const inputType = shouldShowToggle ? (showPassword ? 'text' : 'password') : type;

	const handleTogglePassword = () => {
		if (!isToggleDisabled) {
			setShowPassword(!showPassword);
		}
	};

	if (shouldShowToggle) {
		return (
			<div className="text-input-wrapper">
				<input
					ref={ref}
					className={`TextInput ${className ?? ''}`}
					type={inputType}
					placeholder={placeholder}
					id={formControl?.fieldId}
					disabled={disabled}
					autoComplete={autoComplete}
					{...formInputProps}
				/>
				<CustomLink
					onClick={handleTogglePassword}
					className={`password-toggle ${isToggleDisabled ? 'disabled' : ''}`}
					iconNormalSrc={
						showPassword ? '/images/icons/dark-icons/Eye-Hide.svg' : '/images/icons/dark-icons/Eye.svg'
					}
					iconHoverSrc={
						showPassword ? '/images/icons/dark-icons/Eye-Hide.svg' : '/images/icons/dark-icons/Eye.svg'
					}
					width={30}
					height={30}
					disableHoverOpacity={false}
					ariaLabel={showPassword ? 'Hide password' : 'Show password'}
				/>
			</div>
		);
	}

	return (
		<input
			ref={ref}
			className={`TextInput ${className ?? ''}`}
			type={type}
			placeholder={placeholder}
			id={formControl?.fieldId}
			disabled={disabled}
			autoComplete={autoComplete}
			{...formInputProps}
		/>
	);
});
